<script setup lang="ts">
import type { TQuizMode } from '@/store/quiz/state'
import { computed, nextTick, onMounted, ref } from 'vue'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'

const PocketButton = UIKit.Button
const ModalContainer = UIKit.ModalContainer

withDefaults(defineProps<{
    quizMode?: TQuizMode
    numAnswers?: number
    quizLength?: number
}>(), {
    quizMode: 'quick10',
    numAnswers: 0,
    quizLength: 1,
})

const emit = defineEmits<{
    close: []
    submit: []
    quit: []
}>()

const titleRef = ref<HTMLElement>()

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

onMounted(() => {
    nextTick(() => {
        titleRef.value?.focus()
    })
})

const emitClose = () => {
    emit('close')
}

const emitSubmit = () => {
    emit('submit')
}

const emitQuit = () => {
    emit('quit')
}
</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="quitQuizModal"
            :show-close-button="true"
            :is-dark-mode="isDarkMode"
            @close="emitClose"
        >
            <div class="quit-modal">
                <h1
                    ref="titleRef"
                    class="quit-modal__title"
                    tabindex="-1"
                >
                    Quit Quiz?
                </h1>
                <div
                    v-if="quizMode === 'recallChallenge'"
                    v-dark
                    class="quit-modal__text"
                >
                    You've answered {{ numAnswers }} of {{ quizLength }} question{{ quizLength > 1 ? 's' : '' }}.
                    You will lose your progress if you quit now. If you submit, unanswered questions will be
                    marked incorrect.
                </div>
                <div
                    v-else
                    v-dark
                    class="quit-modal__text"
                >
                    You've answered {{ numAnswers }} {{
                        quizMode === 'timed'
                            ? ''
                            : `of ${quizLength}`
                    }} question{{
                        (quizMode === 'timed' && numAnswers !== 1) || (quizMode !== 'timed' && quizLength !== 1)
                            ? 's'
                            : ''
                    }}. You will lose your progress if you quit now.
                </div>
                <div class="quit-modal__actions">
                    <PocketButton
                        v-if="quizMode === 'recallChallenge'"
                        type="secondary"
                        class="quit-modal__submit-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitSubmit"
                    >
                        Submit Quiz
                    </PocketButton>
                    <PocketButton
                        v-else-if="numAnswers"
                        type="secondary"
                        class="quit-modal__submit-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitSubmit"
                    >
                        Submit {{ numAnswers }} Question{{ numAnswers !== 1 ? 's' : '' }}
                    </PocketButton>
                    <PocketButton
                        class="quit-modal__end-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitQuit"
                    >
                        Quit Quiz
                    </PocketButton>
                </div>
            </div>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.quit-modal {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 35px 41px 26px 38px;
    width: 384px;
    margin: 0 auto;

    &__title {
        font-weight: 600;
        font-size: 18px;
        line-height: 22px;
        margin: 0 0 12px 0;
        outline: none;
    }

    &__text {
        color: $slate-03;
        font-size: 15px;
        line-height: 22px;
        text-align: center;
        margin-bottom: 29px;

        &--dark {
            color: $white;
        }
    }

    &__submit-btn {
        margin-right: 4px;
    }
}
</style>
